// Effects drawer component for displaying character effects

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

/**
 * Backend sends effects in GameClientCharacterEffect format from pc:added,
 * but subsequent /data updates may overwrite with raw format.
 * This component handles both formats gracefully.
 *
 * @typedef {object} CharacterEffect
 * @property {string} source - Effect source slug (e.g. "defense", "savage-attacker")
 * @property {string} [name] - Display name (from backend transform, may be absent in raw format)
 * @property {string} [description] - Effect description (from feat lookup, may be absent in raw format)
 * @property {string} [icon] - Icon identifier (e.g. "shield", "sparkles")
 * @property {boolean} [active] - Whether the effect is active (defaults to true if absent)
 * @property {string} [duration] - Duration (raw format only, e.g. "permanent")
 */

/**
 * @typedef {object} SpellActiveEffect
 * @property {string} source - Spell slug (e.g. "bless")
 * @property {string} [duration] - Duration ("spell", "endOfTargetNextTurn")
 * @property {number} [turnsRemaining] - For turn-based durations
 * @property {string[]} [conditions] - Conditions this effect grants
 */

/**
 * @typedef {object} PlayerCharacter
 * @property {string} id - Character ID
 * @property {string} name - Character name
 * @property {CharacterEffect[]} [effects] - Passive feat / trait effects
 * @property {SpellActiveEffect[]} [spellActiveEffects] - Active spell buffs/debuffs (Bless, Sleep, etc.)
 */

/**
 * Effects drawer component
 * Displays all active effects for player characters
 */
class EffectsDrawer extends BaseComponent {
  static properties = {
    characters: { type: Array },
  };

  static styles = css`
    :host {
      display: block;
    }

    .effects-drawer {
      height: 100%;
      display: flex;
      flex-direction: column;
      color: #fff;
    }

    .drawer-title {
      font-size: 18px;
      font-weight: 600;
      margin: 0 0 16px 0;
      padding-bottom: 12px;
      border-bottom: 1px solid #444;
    }

    .empty-state {
      color: #666;
      font-size: 14px;
      font-style: italic;
      text-align: center;
      padding: 24px;
    }

    .effects-list {
      display: flex;
      flex-direction: column;
      gap: 12px;
      overflow-y: auto;
      flex: 1;
    }

    .effect-item {
      display: flex;
      gap: 12px;
      padding: 12px;
      background: rgba(74, 158, 255, 0.1);
      border: 1px solid rgba(74, 158, 255, 0.3);
      border-radius: 8px;
      transition: all 0.2s;
    }

    .effect-item .effect-icon {
      color: #4a9eff;
      flex-shrink: 0;
      margin-top: 2px;
    }

    .effect-content {
      flex: 1;
      min-width: 0;
    }

    .effect-header {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      gap: 8px;
      margin-bottom: 4px;
    }

    .effect-name {
      font-size: 14px;
      font-weight: 600;
      color: #fff;
    }

    .effect-character {
      font-size: 11px;
      color: #888;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .effect-description {
      font-size: 12px;
      color: #aaa;
      line-height: 1.4;
      margin: 0;
    }

    .effect-status {
      display: inline-block;
      margin-top: 6px;
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #f59e0b;
      background: rgba(245, 158, 11, 0.15);
      border: 1px solid rgba(245, 158, 11, 0.3);
      border-radius: 3px;
      padding: 2px 6px;
    }

    /* Inactive effect styling */
    .effect-item.effect-inactive {
      background: rgba(102, 102, 102, 0.1);
      border-color: rgba(102, 102, 102, 0.3);
    }

    .effect-item.effect-inactive .effect-icon {
      color: #666;
    }

    .effect-item.effect-inactive .effect-name {
      color: #999;
    }

    .effect-item.effect-inactive .effect-description {
      color: #666;
    }

    .icon {
      width: 20px;
      height: 20px;
    }
  `;

  constructor() {
    super();
    /** @type {PlayerCharacter[]} */
    this.characters = [];
  }

  /**
   * Format a source slug into a display name
   * @param {string} source - e.g. "savage-attacker" → "Savage Attacker"
   */
  _formatSourceName(source) {
    return source
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Check if an effect is active (defaults to true if not specified)
   * @param {CharacterEffect} effect
   */
  _isActive(effect) {
    return effect.active !== false;
  }

  get _allEffects() {
    const effects = [];
    for (const character of this.characters) {
      if (character.effects) {
        for (const effect of character.effects) {
          effects.push({
            characterId: character.id,
            characterName: character.name,
            effect,
          });
        }
      }
      // Spell buffs/debuffs (Bless, Shield of Faith, Sleep, etc.) live
      // on a separate array. They share the {source, duration} shape
      // the renderer already handles, plus a sparkles default icon.
      if (character.spellActiveEffects) {
        for (const sae of character.spellActiveEffects) {
          effects.push({
            characterId: character.id,
            characterName: character.name,
            effect: /** @type {CharacterEffect} */ ({
              source: sae.source,
              duration: sae.duration,
              icon: 'sparkles',
            }),
          });
        }
      }
    }
    return effects;
  }

  _renderShieldIcon() {
    return html`
      <svg
        class="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path
          d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
        />
      </svg>
    `;
  }

  _renderSparklesIcon() {
    return html`
      <svg
        class="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path
          d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"
        />
        <path d="M5 3v4" />
        <path d="M19 17v4" />
        <path d="M3 5h4" />
        <path d="M17 19h4" />
      </svg>
    `;
  }

  /** @param {string} [iconName] */
  _getEffectIcon(iconName) {
    switch (iconName) {
      case 'shield':
        return this._renderShieldIcon();
      default:
        return this._renderSparklesIcon();
    }
  }

  /**
   * Decorate an effect chip with the player's per-cast choices, e.g.
   * Resistance becomes "Resistance (fire)". Returns an empty string when
   * the effect carries no choices.
   * @param {*} effect
   */
  _renderChoicesSuffix(effect) {
    const choices = effect?.choices;
    if (!choices) return '';
    const values = Object.values(choices).filter((v) => typeof v === 'string' && v.length > 0);
    if (values.length === 0) return '';
    return ` (${values.join(', ')})`;
  }

  render() {
    return html`
      <div class="effects-drawer">
        <h3 class="drawer-title">Effects</h3>
        ${
          this._allEffects.length === 0
            ? html` <div class="empty-state">No active effects</div> `
            : html`
              <div class="effects-list">
                ${this._allEffects.map(
                  (entry, index) => html`
                    <div
                      class="effect-item ${!this._isActive(entry.effect) ? 'effect-inactive' : ''}"
                      key="${entry.characterId}-${entry.effect.source}-${index}"
                    >
                      <div class="effect-icon">${this._getEffectIcon(entry.effect.icon)}</div>
                      <div class="effect-content">
                        <div class="effect-header">
                          <span class="effect-name"
                            >${entry.effect.name || this._formatSourceName(entry.effect.source)}${this._renderChoicesSuffix(entry.effect)}</span
                          >
                          <span class="effect-character">${entry.characterName}</span>
                        </div>
                        <p class="effect-description">${entry.effect.description || entry.effect.duration || ''}</p>
                        ${!this._isActive(entry.effect) ? html` <span class="effect-status">Inactive</span> ` : ''}
                      </div>
                    </div>
                  `,
                )}
              </div>
            `
        }
      </div>
    `;
  }
}

customElements.define('effects-drawer', EffectsDrawer);
